/**
 * GUAPI Sticky Add to Cart — product page only.
 *
 * Responsibilities:
 *  - stays hidden until the shopper scrolls PAST the theme's own native
 *    "Add to cart" button (found via the real product form), so it never
 *    duplicates the primary buy box while it's in view
 *  - full size picker, sold-out sizes disabled — resolves the EXACT variant
 *    id for the picked size from a per-product JSON variant map (same
 *    pattern as the Quick Shop grid), never a blind default add
 *  - AJAX add-to-cart via /cart/add.js, opens the theme's native
 *    <cart-drawer> so the shopper sees the item landed (checkout flow stays
 *    in the drawer here, since a size was JUST picked — different from the
 *    grid's philosophy of "keep browsing")
 */
(function () {
  "use strict";

  function getVariantMap(root) {
    var script = root.querySelector("[data-guapi-atc-variant-map]");
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error("guapi-atc: failed to parse variant map", e);
      return null;
    }
  }

  function formatMoneyFromCents(cents) {
    return "$" + (cents / 100).toFixed(2);
  }

  function initVisibility(bar) {
    // Anchor: the theme's own native add-to-cart button on the product form.
    // Once it scrolls above the viewport, the sticky bar takes over.
    var nativeForm = document.querySelector('form[action*="/cart/add"]');
    var nativeBtn = nativeForm
      ? nativeForm.querySelector('button[name="add"], button[type="submit"]')
      : null;

    if (!nativeBtn) {
      // No native button found (unusual theme markup) — fail safe by
      // showing the bar after a small scroll so it's never permanently gone.
      window.addEventListener("scroll", function onScroll() {
        if (window.scrollY > 400) {
          bar.setAttribute("data-visible", "true");
          window.removeEventListener("scroll", onScroll);
        }
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          // Native button out of view (scrolled past, above viewport) -> show.
          // Native button visible or below viewport (not reached yet) -> hide.
          var scrolledPast = !entry.isIntersecting && entry.boundingClientRect.top < 0;
          bar.setAttribute("data-visible", scrolledPast ? "true" : "false");
        });
      },
      { threshold: 0 },
    );
    observer.observe(nativeBtn);
  }

  function initSizePicker(bar) {
    var pills = bar.querySelectorAll("[data-guapi-atc-size-pill]");
    var cta = bar.querySelector("[data-guapi-atc-cta]");
    var priceEl = bar.querySelector("[data-guapi-atc-price]");
    if (!pills.length || !cta) return;

    var variantMap = getVariantMap(bar);

    pills.forEach(function (pill) {
      pill.addEventListener("click", function () {
        if (pill.getAttribute("data-disabled") === "true") return;

        var selectedSize = pill.getAttribute("data-size-value");
        pills.forEach(function (p) {
          p.setAttribute("data-selected", p === pill ? "true" : "false");
        });

        var variant = variantMap ? variantMap[selectedSize] : null;
        if (variant && variant.available && variant.id) {
          cta.disabled = false;
          cta.dataset.state = "ready";
          cta.dataset.variantId = variant.id;
          cta.textContent = "Add · " + selectedSize;
          if (priceEl && variant.price) {
            priceEl.textContent = formatMoneyFromCents(variant.price);
          }
        } else {
          cta.disabled = true;
          cta.dataset.state = "soldout";
          delete cta.dataset.variantId;
          cta.textContent = "Sold out";
        }
      });
    });
  }

  function showToast(bar, text) {
    var toast = bar.querySelector("[data-guapi-atc-toast]");
    if (!toast) return;
    toast.textContent = text;
    toast.setAttribute("data-visible", "true");
    window.setTimeout(function () {
      toast.setAttribute("data-visible", "false");
    }, 1800);
  }

  function openCartDrawer() {
    var cartDrawerEl = document.querySelector("cart-drawer");
    if (!cartDrawerEl) return;
    fetch("/cart?section_id=cart-drawer")
      .then(function (res) {
        return res.text();
      })
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var newBody = doc.getElementById("CartDrawer-Body");
        var curBody = document.getElementById("CartDrawer-Body");
        if (newBody && curBody) {
          curBody.innerHTML = newBody.innerHTML;
        }
        if (typeof cartDrawerEl.open === "function") {
          cartDrawerEl.open();
        } else {
          cartDrawerEl.classList.add("active", "animate");
          document.body.classList.add("overflow-hidden");
        }
      })
      .catch(function () {
        if (cartDrawerEl.open) cartDrawerEl.open();
      });
  }

  function refreshHeaderCartCount(cart) {
    document
      .querySelectorAll("[data-cart-count], .cart-count, #cart-icon-bubble")
      .forEach(function (el) {
        if ("textContent" in el) el.textContent = cart.item_count;
      });
  }

  function initAddToCart(bar) {
    var cta = bar.querySelector("[data-guapi-atc-cta]");
    if (!cta) return;

    cta.addEventListener("click", function () {
      if (cta.disabled || cta.dataset.state === "soldout") return;

      var variantId = cta.dataset.variantId;
      if (!variantId) return; // no size picked yet on a sized product

      cta.dataset.state = "adding";
      var originalLabel = cta.textContent;
      cta.textContent = "Adding…";

      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("Add to cart failed");
          return res.json();
        })
        .then(function () {
          cta.dataset.state = "added";
          cta.textContent = "Added";
          showToast(bar, "Added to cart");

          fetch("/cart.js")
            .then(function (res) {
              return res.json();
            })
            .then(refreshHeaderCartCount)
            .catch(function () {});

          openCartDrawer();

          window.setTimeout(function () {
            cta.dataset.state = "ready";
            cta.textContent = originalLabel;
          }, 1600);
        })
        .catch(function (err) {
          console.error("guapi-atc: add to cart failed", err);
          cta.dataset.state = "ready";
          cta.textContent = originalLabel;
          showToast(bar, "Could not add to cart — try again");
        });
    });
  }

  function init() {
    var bar = document.querySelector("[data-guapi-atc]");
    if (!bar) return;

    initVisibility(bar);
    initSizePicker(bar);
    initAddToCart(bar);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
